import { apiClient } from '../lib/apiClient';

export const KH_SUPPLY_METHODS = ['calcium_reactor', 'sangokai', 'balling_light'] as const;
export type KhSupplyMethod = (typeof KH_SUPPLY_METHODS)[number];

/** Mirrors the API (src/types/khSupply.ts) — snake_case like the columns. */
export interface KhSupplyConfig {
  readonly method: KhSupplyMethod;
  readonly effluent_ml_per_min: number | null;
  readonly effluent_kh_dkh: number | null;
  readonly effluent_ca_mg_l: number | null;
  /** Calcium reactor running hours per day (null = 24). */
  readonly reactor_hours_per_day?: number | null;
  readonly kh_solution_ml_per_day: number | null;
  readonly updated_at: string;
}

export interface KhSupplyConfigInput {
  readonly method: KhSupplyMethod;
  readonly effluent_ml_per_min?: number | null;
  readonly effluent_kh_dkh?: number | null;
  readonly effluent_ca_mg_l?: number | null;
  readonly reactor_hours_per_day?: number | null;
  readonly kh_solution_ml_per_day?: number | null;
}

export type KhVolumeSource = 'net_volume' | 'volume_liters' | 'gross_volume';

export interface KhUsage {
  readonly configured: boolean;
  readonly method: KhSupplyMethod | null;
  readonly volume_liters: number | null;
  readonly volume_source: KhVolumeSource | null;
  readonly supply_dkh_liters_per_day: number | null;
  readonly supply_dkh_per_day: number | null;
  readonly supply_ca_mg_l_per_day: number | null;
  readonly ca_estimated: boolean;
  readonly kh_trend_dkh_per_day: number | null;
  readonly kh_trend_measurements: number;
  readonly usage_dkh_per_day: number | null;
  readonly latest_kh_dkh: number | null;
  readonly tank_kh_assumed: boolean;
  readonly tank_ca_assumed: boolean;
  /** Calcium reactor: effluent litres per day and the tank KH subtracted (for the shown calculation). */
  readonly effluent_liters_per_day?: number | null;
  readonly tank_kh_reference_dkh?: number | null;
  readonly effluent_kh_dkh?: number | null;
}

export const khSupplyQueryKey = (aquariumId: string) => ['khSupply', aquariumId] as const;
export const khUsageQueryKey = (aquariumId: string) => ['khUsage', aquariumId] as const;

export async function getKhSupplyConfig(aquariumId: string): Promise<KhSupplyConfig | null> {
  const json = await apiClient.get<{ data: KhSupplyConfig | null }>(`/api/v1/aquariums/${aquariumId}/kh-supply`);
  return json.data;
}

export async function saveKhSupplyConfig(aquariumId: string, input: KhSupplyConfigInput): Promise<KhSupplyConfig> {
  const json = await apiClient.put<{ data: KhSupplyConfig }>(`/api/v1/aquariums/${aquariumId}/kh-supply`, input);
  return json.data;
}

export async function deleteKhSupplyConfig(aquariumId: string): Promise<void> {
  await apiClient.delete(`/api/v1/aquariums/${aquariumId}/kh-supply`);
}

export async function getKhUsage(aquariumId: string): Promise<KhUsage> {
  const json = await apiClient.get<{ data: KhUsage }>(`/api/v1/aquariums/${aquariumId}/kh-supply/usage`);
  return json.data;
}
